import React, {
  createContext,
  useState,
  useEffect,
  useRef,
  useContext,
} from "react";
import ReconnectingWebSocket from "reconnecting-websocket";
import { TeamsUserCredential } from "@microsoft/teamsfx";
import { useSelector } from "react-redux";
import HPBXService from "../services/HPBXService";

export const AppSyncContext = createContext(null);

export const AppSyncProvider = ({ children }) => {
  //hardcoded for now
  const REALTIME_DOMAIN =
    "ifyedtedmjg7deue7okadefap4.appsync-realtime-api.ap-southeast-2.amazonaws.com";
  const HTTP_DOMAIN =
    "ifyedtedmjg7deue7okadefap4.appsync-api.ap-southeast-2.amazonaws.com";

  const [error, setError] = useState("");
  const [transcriptions, setTranscriptions] = useState(new Map()); // Map of callid -> transcription
  const [activeTranscription, setActiveTranscription] = useState({}); // callid
  const [subscribedCalls, setSubscribedCalls] = useState(new Map()); // Map of callid -> call

  const rwsRef = useRef(null);

  const profile = useSelector((state) => state.user.userProfile);

  /*
   * Subscribe to a call for transcription.
   * @param {Object} call - The call object containing call details.
   * @param {string} agent - The agent id.
   * @param {string} spy_callid - The callid of the spy call, used to tie transcriptions to call details.
   */

  const subscribeToCall = async (call, agent, spy_callid) => {
    // Don't resubscribe if already subscribed
    if (subscribedCalls.has(spy_callid)) {
      console.error(`Call ${spy_callid} is already subscribed.`);
      return;
    }

    call.agent = agent;

    const number = call.dtype === "external" ? call.snumber : call.dnumber;
    const customer = profile.hpbx[0].customer;
    const channel = `/transcribe/au/${customer}/${number}/${agent}`;
    console.error(`Subscribing to channel ${channel} for call ${spy_callid}`);
    await subscribeToChannel(channel);

    setSubscribedCalls((prev) => {
      const newMap = new Map(prev);
      newMap.set(spy_callid, call);
      return newMap;
    });

    console.error("setting active transcription", spy_callid);
    setActiveTranscription(spy_callid);
  };

  const subscribeToChannel = async (channel) => {
    const { authToken } = (await HPBXService.getAppsyncToken()).data;

    const subscribeMessage = {
      id: crypto.randomUUID(),
      type: "subscribe",
      channel: channel,
      authorization: {
        authorization: "Bearer " + authToken,
      },
    };
    rwsRef.current?.send(JSON.stringify(subscribeMessage));
  };

  const unsubscribe = (channel) => {
    console.error(`TODO: UNSUB from ${channel}`);
    // send unsubscribe message
    // delete key from subscribedCalls
  };

  useEffect(() => {
    const options = {
      connectionTimeout: 1000,
      maxRetries: 10,
    };

    let isMounted = true;

    const initWebSocket = async () => {
      try {
        const { authToken } = (await HPBXService.getAppsyncToken()).data;
        const authorization = {
          host: HTTP_DOMAIN,
          Authorization: "Bearer " + authToken,
        };

        const rws = new ReconnectingWebSocket(
          `wss://${REALTIME_DOMAIN}/event/realtime`,
          [getAuthProtocol(authorization), "aws-appsync-event-ws"],
          options
        );

        rws.addEventListener("open", () => {
          console.log("Connection to AppSync opened");
        });

        rws.addEventListener("close", () => {
          console.log("Connection to AppSync closed");
        });

        rws.addEventListener("message", (event) => {
          const data = JSON.parse(event.data);
          if (data.type === "data") {
            const eventData = JSON.parse(data.event);
            console.error(
              "new websocket message from",
              eventData.call_id,
              "updating transcriptions"
            );
            setTranscriptions((prev) => {
              const newMap = new Map(prev);
              newMap.set(eventData.call_id, eventData.transcription);
              return newMap;
            });
          }
        });

        rws.addEventListener("error", (event) => {
          console.error("AppSync error:", event);
        });

        if (!isMounted) {
          rws.close();
          return;
        }

        rwsRef.current = rws;
      } catch (err) {
        console.error("Failed to initialize AppSync websocket:", err);
        if (isMounted) {
          //setError("Unable to connect to transcription service.");
        }
      }
    };

    initWebSocket();

    return () => {
      isMounted = false;
      rwsRef.current?.close();
    };
  }, []);

  return (
    <AppSyncContext.Provider
      value={{
        subscribeToCall,
        unsubscribe,
        transcriptions, // Map
        subscribedCalls, // Map
        activeTranscription,
        setActiveTranscription,
      }}
    >
      {error && <div className="alert alert-danger">{error}</div>}
      {children}
    </AppSyncContext.Provider>
  );
};

// helper functoin taken from https://docs.aws.amazon.com/appsync/latest/eventapi/event-api-websocket-protocol.html
function getBase64URLEncoded(authorization) {
  return btoa(JSON.stringify(authorization))
    .replace(/\+/g, "-") // Convert '+' to '-'
    .replace(/\//g, "_") // Convert '/' to '_'
    .replace(/=+$/, ""); // Remove padding `=`
}

// helper functoin taken from https://docs.aws.amazon.com/appsync/latest/eventapi/event-api-websocket-protocol.html
function getAuthProtocol(authorization) {
  const header = getBase64URLEncoded(authorization);
  return `header-${header}`;
}
